/**
 * The three lanes, rendered.
 *
 * Each is a thin projection of its hook onto {@link QueueSection}: the hooks own
 * "what is waiting", this file owns "how it reads". Kept apart from the page so
 * the page is a layout and each lane can be read (and tested) on its own.
 */
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Badge, Button, ChainAvatar, CriticalityBadge } from "@authe/ui-core";
import { formatRelativeTime } from "@authe/fe-commons/time";
import { shortId } from "@authe/fe-commons/display";
import { ROUTES } from "@/config";
import { QueueRow, QueueSection } from "./QueueSection";
import { useActiveCases, useFiredAlerts, useLiveWatches } from "./useQueueLanes";

function ago(atMs: number): string {
  return atMs > 0 ? formatRelativeTime(atMs, { unit: "milliseconds" }) : "";
}

export function FiredAlertsLane(): React.ReactElement | null {
  const lane = useFiredAlerts();
  return (
    <QueueSection
      title="Alerts that fired"
      description="Rules that matched, newest first."
      outcome={lane.outcome}
      more={lane.more}
      seeAll={{ to: ROUTES.alerts, label: "All alerts" }}
      retry={lane.retry}
      error={lane.error}
      failedTitle="Couldn't read the alert history"
      empty={{
        title: "Nothing has fired",
        description:
          "No rule has matched in the window this reads. That is a finding when you have rules; if you have none, it is not.",
        action: (
          <Button asChild size="sm" variant="outline">
            <Link to={ROUTES.alerts}>Review your rules</Link>
          </Button>
        ),
      }}
    >
      {lane.items.map((alert) => (
        <QueueRow
          key={alert.id}
          to={alert.txRef ? `${ROUTES.transactions}/${alert.txRef}` : ROUTES.alerts}
          // The shared pill in its dot form, not a fourth local colour map.
          // The hand-rolled one was `aria-hidden`, so the only thing on the row
          // that said how serious the alert was reached nobody who could not
          // see it — the rest of the row is a rule name, a short hash and a
          // relative time. It also coloured `CRITICAL` from a `risk-severe`
          // token, which this design system does not have (the ramp ends in
          // `critical`; only the risk BUCKET ladder ends in "severe"), so the
          // single most serious dot on the landing page rendered with no
          // colour at all. `statusTokens.test.ts` now guards that.
          marker={<CriticalityBadge level={alert.criticality} dot />}
          lead={alert.rule}
          meta={alert.txHash ? shortId(alert.txHash) : "no transaction"}
          when={ago(alert.atMs)}
        />
      ))}
    </QueueSection>
  );
}

export function ActiveCasesLane(): React.ReactElement | null {
  const lane = useActiveCases();
  return (
    <QueueSection
      title="Your cases"
      description="Open investigations, most recent activity first."
      outcome={lane.outcome}
      more={lane.more}
      seeAll={{ to: ROUTES.investigations, label: "All investigations" }}
      retry={lane.retry}
      error={lane.error}
      failedTitle="Couldn't read your cases"
      empty={{
        title: "No open cases",
        description: "A case is where a trail, its evidence and your conclusion live together.",
        action: (
          <Button asChild size="sm">
            <Link to={ROUTES.investigations}>Start a case</Link>
          </Button>
        ),
      }}
    >
      {lane.items.map((item) => (
        <QueueRow
          key={item.id}
          to={`${ROUTES.investigations}/${item.id}`}
          lead={item.title}
          meta={[
            item.watches > 0 ? `${item.watches} watch${item.watches === 1 ? "" : "es"}` : null,
            item.exhibits > 0 ? `${item.exhibits} pinned` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
          when={ago(item.updatedAtMs)}
        />
      ))}
    </QueueSection>
  );
}

export function LiveWatchesLane(): React.ReactElement | null {
  const lane = useLiveWatches();
  return (
    <QueueSection
      title="Watches"
      description="What the platform is following for you."
      outcome={lane.outcome}
      more={lane.more}
      seeAll={{ to: ROUTES.watches, label: "All watches" }}
      retry={lane.retry}
      error={lane.error}
      failedTitle="Couldn't read your watches"
      empty={{
        title: "Nothing is being watched",
        description: "A watch keeps looking after you close the tab — an address, a hash, or a situation.",
        action: (
          <Button asChild size="sm" variant="outline">
            <Link to={ROUTES.watches}>Add a watch</Link>
          </Button>
        ),
      }}
    >
      {lane.items.map((watch) => (
        <QueueRow
          key={watch.id}
          to={`${ROUTES.watches}/${watch.id}`}
          marker={watch.chain ? <ChainAvatar chain={watch.chain} size="sm" showName={false} /> : undefined}
          lead={watch.label}
          meta={
            // Every state except the unremarkable one: a badge on every row
            // would make the row that needs reading look like the rest.
            watch.status.label === "Active" ? undefined : (
              <Badge variant={watch.status.variant} className="text-[10px] uppercase">
                {watch.status.label}
              </Badge>
            )
          }
        />
      ))}
    </QueueSection>
  );
}
